import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";

import { ApiInterceptorError } from "@classes/ApiInterceptorError";
import { User } from "@contexts/Auth";
import { featureFlags } from "@settings";

import {
  useApiAuthCheckpoint,
  useApiAuthPermissoesUsuario,
} from "@hooks/api/auth";
import useAuth from "@hooks/useAuth";
import { useCorban } from "@hooks/useCorban";
import { PRODUCT_CODE } from "@utils/enums";
import { getProductTypeFromProductCode } from "@utils/product";

type PermissionsContext = {
  permissions: Record<string, string>;
  routes: Record<string, boolean>;
  allowedRoutes: string[];
  homeRoute: string;
  isReady: boolean;
  hasPermission: (permission: string) => boolean;

  loading: boolean;
};

export const PermissionsContext = createContext<PermissionsContext>({
  permissions: {},
  routes: {},
  allowedRoutes: [],
  homeRoute: "",
  isReady: false,
  hasPermission: () => false,
  loading: true,
});

export const PermissionsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [
    fetchPermissoesUsuario,
    {
      data: fetchPermissionsData,
      loading: permissionsLoading,
      reset: resetFetchPermissions,
    },
  ] = useApiAuthPermissoesUsuario();
  const [
    fetchCheckpoint,
    {
      data: fetchCheckpointData,
      loading: checkpointLoading,
      reset: resetFetchCheckpoint,
    },
  ] = useApiAuthCheckpoint();

  const {
    user,
    updateUser,
    setProducts,
    hasProduct,
    loading: userLoading,
  } = useAuth();
  const { isReady: isReadyCorban } = useCorban();
  const isReady =
    !!fetchPermissionsData &&
    (featureFlags.enableConsignadoPublico ? !!fetchCheckpointData : true);

  const [permissions, setPermissions] = useState<
    PermissionsContext["permissions"]
  >({});
  const [loading, setLoading] = useState(true);
  const clearData = useCallback(() => {
    setPermissions({});
    resetFetchPermissions();
    resetFetchCheckpoint();
  }, [resetFetchPermissions, resetFetchCheckpoint]);

  const hasPermission = useCallback(
    (permission: string) => !!permissions[permission],
    [permissions],
  );

  const routes: Record<string, boolean> = useMemo(() => {
    return {
      "/servicos": hasPermission("add_contrato"),
      "/cartao/cartao-beneficio":
        hasProduct(PRODUCT_CODE.CARTAO_BENEFICIO) &&
        hasPermission("add_contrato"),
      "/cartao/agregacao-margem":
        hasProduct(PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO) &&
        hasPermission("add_contrato"),
      "/saque-complementar":
        hasProduct(PRODUCT_CODE.CARTAO_BENEFICIO) &&
        hasPermission("add_contrato"),
      "/contratos": true,
      "/selecao-corban": true,
      "/termos-de-uso": true,
      "/politica-de-privacidade": true,

      "/sem-acesso": true,

      "/nao-me-perturbe": hasPermission("add_contrato"),

      // Submenu keys
      "/cartao": [
        PRODUCT_CODE.CARTAO_BENEFICIO,
        PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO,
      ].some(hasProduct),

      "/consignado": [PRODUCT_CODE.CONSIGNADO_PRIVADO].some(hasProduct),
      "/consignado/consignado-privado":
        hasProduct(PRODUCT_CODE.CONSIGNADO_PRIVADO) &&
        hasPermission("add_contrato"),

      "/consignado/acompanhamento-proposta":
        hasProduct(PRODUCT_CODE.CONSIGNADO_PRIVADO) &&
        hasPermission("add_contrato"),

      "/menu-usuarios":
        !!user?.allowCreateUser &&
        [
          "happy_can_create_user",
          "happy_can_view_user",
          "happy_can_change_user",
        ].some(hasPermission),
      "/usuarios":
        !!user?.allowCreateUser && hasPermission("happy_can_create_user"),
      "/usuarios/solicitacao":
        !!user?.allowCreateUser && hasPermission("happy_can_create_user"),
      "/usuarios/novo":
        !!user?.allowCreateUser && hasPermission("happy_can_create_user"),
    };
  }, [user, hasProduct, hasPermission]);

  const allowedRoutes = useMemo(() => {
    return Object.keys(routes).filter((route) => routes[route]);
  }, [routes]);

  const homeRoute = useMemo(() => allowedRoutes[0] || "", [allowedRoutes]);

  const fetchPermissions = useCallback(async () => {
    try {
      const results = await Promise.all([
        fetchPermissoesUsuario(),
        (async function () {
          if (featureFlags.enableConsignadoPublico) {
            return await fetchCheckpoint();
          }
          return null;
        })(),
      ]);

      const { data } = results[0];
      const { data: checkoutData } = results[1] ?? { data: { slug: null } };

      const userData: Partial<User> = {
        name: data.name,
        uniqueId: data.unique_id,
        identifier: data.identifier,
        enableFaceMatch: data.enable_face_match,
        isChecked: data.is_checked,
        checkUrl: data.check_url,
        supportChat: data.possui_chat_suporte,
        allowIlliterate: !!data.corban?.permite_analfabeto,
        allowLegalRepresentative: !!data.corban?.permite_representante,
        allowCardLegalRepresentative:
          !!data.corban?.permite_representante_cartao,
        allowCreateUser: !!data.corban?.permite_criar_usuario_originacao,
        id: data.id,
        checkoutSlug: checkoutData.slug || null,
      };

      updateUser(userData);

      setPermissions(
        data.permissions.reduce(
          (permissionsHashList, permission) => ({
            ...permissionsHashList,
            [permission]: true,
          }),
          {},
        ),
      );

      setProducts(
        data.produtos.map((product) => ({
          nome: product.nome,
          tipo_produto: getProductTypeFromProductCode(product.cd_produto),
          codigo_produto: product.cd_produto,
        })),
      );
    } catch (e) {
      if (
        !(e instanceof ApiInterceptorError) ||
        !axios.isCancel(e.sourceError)
      ) {
        clearData();
      }
    } finally {
      setLoading(false);
    }
  }, [
    fetchPermissoesUsuario,
    fetchCheckpoint,
    updateUser,
    setProducts,
    clearData,
  ]);

  useEffect(() => {
    if (!userLoading) {
      if (user?.refreshToken) {
        if (isReadyCorban) void fetchPermissions();
      } else {
        clearData();
        setLoading(false);
      }
    }
  }, [
    user?.refreshToken,
    userLoading,
    fetchPermissions,
    clearData,
    isReadyCorban,
  ]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        routes,
        allowedRoutes,
        homeRoute,
        isReady,
        hasPermission,
        loading: loading || permissionsLoading || checkpointLoading,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
